// /tools/youtube-timestamp-generator, /tools/youtube-transcript, /tools/youtube-video-summarizer:
// paste a link, sign up free (one click), and the link is waiting in the Workspace composer.
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { AuthContext } from '../../AuthContext';
import Seo, { SITE_URL } from '../site/Seo';
import CtaBand from '../site/CtaBand';
import Faq from '../site/Faq';
import DemoCard from '../useCases/DemoCard';
import { FREE_NOTE } from '../useCases/UseCasePage';
import { EXAMPLES } from '../../myWorkspace/onboarding/examples';
import CHAPTERS from '../useCases/exampleChapters.json';
import { extractYouTubeVideoId } from '../../myWorkspace/workspaceComponents/youtubeLinks';
import { savePendingLink } from '../../myWorkspace/pendingLink';
import { track } from '../../analytics/track';
import { TOOLS, toolBySlug } from './toolData';
import '../useCases/useCases.css';

function Sample({ sample }) {
  if (sample.kind === 'chapters') {
    const ex = EXAMPLES[sample.example];
    return <DemoCard videoId={ex.videoId} title={ex.title} by={ex.by} seconds={ex.seconds} chapters={CHAPTERS[sample.example]} caption="Real timestamps from Bumpups for a public YouTube video." />;
  }
  if (sample.kind === 'transcript') {
    return <DemoCard videoId={sample.video.videoId} title={sample.video.title} transcript={sample.lines} caption="The first lines of a transcript Bumpups exported for a public YouTube video." />;
  }
  return <DemoCard videoId={sample.video.videoId} title={sample.video.title} question={sample.question} answer={sample.answer} caption="A real summary from Bumpups for a public YouTube video." />;
}

export default function YouTubeToolPage({ slug }) {
  const tool = toolBySlug(slug);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const videoId = extractYouTubeVideoId(url.trim());

  const submit = e => {
    e.preventDefault();
    if (!videoId) { setError('That doesn’t look like a YouTube link. Paste the video’s address, like https://youtu.be/…'); return; }
    savePendingLink(url.trim(), tool.tool);
    track('tool_started', { tool: tool.tool, signedIn: !!currentUser });
    navigate(currentUser ? '/workspace' : '/signup');
  };

  return (
    <>
      <Seo
        title={tool.seo.title}
        description={tool.seo.description}
        jsonLd={{
          '@type': 'SoftwareApplication',
          name: tool.label,
          url: `${SITE_URL}/tools/${tool.slug}`,
          applicationCategory: 'MultimediaApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        }}
      />
      <section className="uc-hero mk-hero-bg">
        <div className="mk-container">
          <p className="mk-eyebrow"><FontAwesomeIcon icon={tool.icon} /> Free tool</p>
          <h1 className="mk-h1 uc-title">{tool.h1}</h1>
          <p className="mk-lead">{tool.lead}</p>
          <form className="tl-form" onSubmit={submit}>
            <input
              className="tl-input"
              value={url}
              onChange={e => { setUrl(e.target.value); setError(''); }}
              placeholder="Paste a YouTube link"
              aria-label="YouTube link"
              inputMode="url"
              autoComplete="off"
            />
            <button type="submit" className="mk-btn mk-btn--primary">
              {tool.cta} <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
            </button>
          </form>
          {videoId && (
            <div className="tl-preview">
              <img src={`https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`} alt="" width="96" height="54" />
              <span>{currentUser ? 'It’ll be waiting in your Workspace.' : 'Sign up free in one click, and it’ll be waiting in your Workspace.'}</span>
            </div>
          )}
          {error && <p className="tl-error" role="alert">{error}</p>}
          <p className="mk-note">{FREE_NOTE}</p>
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head"><h2 className="mk-h2">{tool.sample.title}</h2></div>
          <Sample sample={tool.sample} />
        </div>
      </section>

      <section className="mk-section mk-section--tight">
        <div className="mk-container">
          <div className="mk-section-head"><h2 className="mk-h2">How it works</h2></div>
          <div className="mk-grid mk-grid--3">
            {tool.steps.map(([title, text], i) => (
              <div key={title} className="mk-card">
                <span className="uc-step-num">{i + 1}</span>
                <h3 className="mk-h3">{title}</h3>
                <p className="mk-text">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Faq title="Common questions" items={tool.faq} />
      <MoreTools current={tool.slug} />
      <CtaBand />
    </>
  );
}

export function MoreTools({ current }) {
  return (
    <section className="mk-section mk-section--tight">
      <div className="mk-container">
        <h2 className="mk-h3" style={{ marginBottom: 16 }}>More free tools</h2>
        <div className="mk-grid mk-grid--3">
          {TOOLS.filter(t => t.slug !== current).map(t => (
            <Link key={t.slug} to={`/tools/${t.slug}`} className="mk-card uc-card">
              <span className="mk-icon"><FontAwesomeIcon icon={t.icon} /></span>
              <h3 className="mk-h3">{t.label}</h3>
              <p className="mk-text">{t.summary}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
